import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import CircularProgress from "@material-ui/core/CircularProgress";
import InputAdornment from "@material-ui/core/InputAdornment";

import PersonOutlineOutlinedIcon from "@material-ui/icons/PersonOutlineOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AttachMoneyOutlinedIcon from "@material-ui/icons/AttachMoneyOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import NotesOutlinedIcon from "@material-ui/icons/NotesOutlined";
import PersonAddAltOutlinedIcon from "@material-ui/icons/PersonAddOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { TAG_KEYS, CH_KEYS, CH, PRIO_KEYS, PRIO } from "./kanban/tokens";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  formControl: {
    marginTop: theme.spacing(1),
    minWidth: 120,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  deleteButton: {
    marginRight: "auto",
  },
}));

const initialState = {
  name: "",
  number: "",
  email: "",
  value: "",
  stageId: "",
  whatsappId: "",
  userId: "",
  notes: "",
  tag: "",
  channel: "",
  priority: "",
};

const LeadModal = ({
  open,
  onClose,
  leadId,
  stages,
  connections,
  attendants,
  defaultStageId,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  // Adorno de ícone (Outlined) no início do campo — visual moderno consistente.
  const adorn = (Icon) => ({
    startAdornment: (
      <InputAdornment position="start">
        <Icon fontSize="small" style={{ color: "#98A2B3" }} />
      </InputAdornment>
    ),
  });

  const [lead, setLead] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchLead = async () => {
      if (!open) return;

      if (!leadId) {
        setLead({
          ...initialState,
          stageId: defaultStageId || "",
        });
        return;
      }

      try {
        const { data } = await api.get(`/crm/leads/${leadId}`);
        if (!isMounted.current) return;
        setLead({
          name: data.name || "",
          number: data.number || "",
          email: data.email || "",
          value: data.value != null ? String(data.value) : "",
          stageId: data.stageId || "",
          whatsappId: data.whatsappId || "",
          userId: data.userId || "",
          notes: data.notes || "",
          tag: data.tag || "",
          channel: data.channel || "",
          priority: data.priority || "",
        });
      } catch (err) {
        toastError(err);
      }
    };

    fetchLead();
  }, [leadId, open, defaultStageId]);

  const handleClose = () => {
    onClose();
    setLead(initialState);
  };

  const handleChange = (field) => (event) => {
    setLead((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setLoading(true);
    const payload = {
      name: lead.name,
      number: lead.number,
      email: lead.email || null,
      value: lead.value === "" ? 0 : Number(lead.value),
      stageId: lead.stageId || null,
      whatsappId: lead.whatsappId || null,
      userId: lead.userId || null,
      notes: lead.notes || null,
      tag: lead.tag || null,
      channel: lead.channel || null,
      priority: lead.priority || null,
    };
    try {
      if (leadId) {
        await api.put(`/crm/leads/${leadId}`, payload);
      } else {
        await api.post("/crm/leads", payload);
      }
      toast.success(i18n.t("crmKanban.leadModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/crm/leads/${leadId}`);
      toast.success(i18n.t("crmKanban.leadModal.deleted"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <ConfirmationModal
        title={i18n.t("crmKanban.leadModal.confirmDeleteTitle")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={handleDelete}
      >
        {i18n.t("crmKanban.leadModal.confirmDeleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              verticalAlign: "middle",
            }}
          >
            {leadId ? (
              <EditOutlinedIcon fontSize="small" />
            ) : (
              <PersonAddAltOutlinedIcon fontSize="small" />
            )}
            {leadId
              ? i18n.t("crmKanban.leadModal.title.edit")
              : i18n.t("crmKanban.leadModal.title.add")}
          </span>
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.multFieldLine}>
            <TextField
              label={i18n.t("crmKanban.leadModal.form.name")}
              name="name"
              autoFocus
              value={lead.name}
              onChange={handleChange("name")}
              variant="outlined"
              margin="dense"
              fullWidth
              InputProps={adorn(PersonOutlineOutlinedIcon)}
            />
          </div>
          <div className={classes.multFieldLine}>
            <TextField
              label={i18n.t("crmKanban.leadModal.form.number")}
              name="number"
              value={lead.number}
              onChange={handleChange("number")}
              placeholder="5513912344321"
              variant="outlined"
              margin="dense"
              fullWidth
              InputProps={adorn(WhatsAppIcon)}
            />
            <TextField
              label={i18n.t("crmKanban.leadModal.form.value")}
              name="value"
              type="number"
              value={lead.value}
              onChange={handleChange("value")}
              variant="outlined"
              margin="dense"
              fullWidth
              InputProps={adorn(AttachMoneyOutlinedIcon)}
            />
          </div>
          <TextField
            label={i18n.t("crmKanban.leadModal.form.email")}
            name="email"
            value={lead.email}
            onChange={handleChange("email")}
            variant="outlined"
            margin="dense"
            fullWidth
            InputProps={adorn(MailOutlineIcon)}
          />
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="stage-select-label">
              {i18n.t("crmKanban.leadModal.form.stage")}
            </InputLabel>
            <Select
              labelId="stage-select-label"
              value={lead.stageId}
              onChange={handleChange("stageId")}
              label={i18n.t("crmKanban.leadModal.form.stage")}
            >
              {stages.map((stage) => (
                <MenuItem key={stage.id} value={stage.id}>
                  {stage.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="tag-select-label">
              {i18n.t("crmKanban.leadModal.form.tag")}
            </InputLabel>
            <Select
              labelId="tag-select-label"
              value={lead.tag}
              onChange={handleChange("tag")}
              label={i18n.t("crmKanban.leadModal.form.tag")}
            >
              <MenuItem value="">&nbsp;</MenuItem>
              {TAG_KEYS.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="channel-select-label">
              {i18n.t("crmKanban.leadModal.form.channel")}
            </InputLabel>
            <Select
              labelId="channel-select-label"
              value={lead.channel}
              onChange={handleChange("channel")}
              label={i18n.t("crmKanban.leadModal.form.channel")}
            >
              <MenuItem value="">&nbsp;</MenuItem>
              {CH_KEYS.map((c) => (
                <MenuItem key={c} value={c}>
                  {CH[c][0]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="priority-select-label">
              {i18n.t("crmKanban.leadModal.form.priority")}
            </InputLabel>
            <Select
              labelId="priority-select-label"
              value={lead.priority}
              onChange={handleChange("priority")}
              label={i18n.t("crmKanban.leadModal.form.priority")}
            >
              <MenuItem value="">&nbsp;</MenuItem>
              {PRIO_KEYS.map((p) => (
                <MenuItem key={p} value={p}>
                  {PRIO[p][0]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="connection-select-label">
              {i18n.t("crmKanban.leadModal.form.connection")}
            </InputLabel>
            <Select
              labelId="connection-select-label"
              value={lead.whatsappId}
              onChange={handleChange("whatsappId")}
              label={i18n.t("crmKanban.leadModal.form.connection")}
            >
              <MenuItem value="">&nbsp;</MenuItem>
              {connections.map((conn) => (
                <MenuItem key={conn.id} value={conn.id}>
                  {conn.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl
            variant="outlined"
            margin="dense"
            fullWidth
            className={classes.formControl}
          >
            <InputLabel id="attendant-select-label">
              {i18n.t("crmKanban.leadModal.form.attendant")}
            </InputLabel>
            <Select
              labelId="attendant-select-label"
              value={lead.userId}
              onChange={handleChange("userId")}
              label={i18n.t("crmKanban.leadModal.form.attendant")}
            >
              <MenuItem value="">&nbsp;</MenuItem>
              {attendants.map((att) => (
                <MenuItem key={att.id} value={att.id}>
                  {att.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={i18n.t("crmKanban.leadModal.form.notes")}
            name="notes"
            value={lead.notes}
            onChange={handleChange("notes")}
            variant="outlined"
            margin="dense"
            fullWidth
            multiline
            rows={3}
            InputProps={adorn(NotesOutlinedIcon)}
          />
        </DialogContent>
        <DialogActions>
          {leadId && (
            <Button
              onClick={() => setConfirmOpen(true)}
              color="secondary"
              variant="outlined"
              className={classes.deleteButton}
              disabled={loading}
              startIcon={<DeleteOutlineIcon />}
            >
              {i18n.t("crmKanban.leadModal.buttons.delete")}
            </Button>
          )}
          <Button
            onClick={handleClose}
            color="secondary"
            variant="outlined"
            disabled={loading}
            startIcon={<CloseIcon />}
          >
            {i18n.t("crmKanban.leadModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
            disabled={loading}
            startIcon={<CheckIcon />}
          >
            {leadId
              ? i18n.t("crmKanban.leadModal.buttons.okEdit")
              : i18n.t("crmKanban.leadModal.buttons.okAdd")}
            {loading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LeadModal;
